import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "@/components/site/Footer";
import { blogPosts, getPostBySlug } from "@/data/blog-posts";
import { SITE_URL, SITE_NAME } from "@/lib/seo-data";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const url = `${SITE_URL}/blog/${post.slug}`;
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.cover,
    author: { "@type": "Organization", name: post.author },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    datePublished: post.date,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <main className="bg-background overflow-x-hidden">
      <Helmet>
        <title>{`${post.title} — ${SITE_NAME}`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="article:published_time" content={post.date} />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={post.cover}
          alt={post.coverAlt}
          width={1920}
          height={1080}
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/60 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70 hidden sm:inline">Sojourns</span>
            </Link>
            <Link
              to="/blog"
              className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors"
            >
              ← All articles
            </Link>
          </div>
          <div className="container pt-20 pb-24 md:pt-28 md:pb-36 max-w-4xl">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-background/70 mb-6">
              <time dateTime={post.date}>{formatDate(post.date)}</time>
              <span>·</span>
              <span>{post.readingMinutes} min read</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.02] text-balance">
              {post.title}
            </h1>
            <p className="mt-8 max-w-2xl text-background/80 font-light leading-relaxed text-lg">
              {post.excerpt}
            </p>
          </div>
        </div>
      </header>

      <article className="py-20 md:py-28">
        <div className="container max-w-2xl">
          {post.body.map((block, i) => {
            if (block.type === "h2") {
              return (
                <h2
                  key={i}
                  className="font-display text-3xl md:text-4xl text-foreground mt-14 mb-5 leading-tight"
                >
                  {block.text}
                </h2>
              );
            }
            if (block.type === "h3") {
              return (
                <h3
                  key={i}
                  className="font-display text-xl md:text-2xl text-foreground mt-10 mb-3"
                >
                  {block.text}
                </h3>
              );
            }
            if (block.type === "ul") {
              return (
                <ul
                  key={i}
                  className="list-disc pl-6 space-y-3 text-foreground/85 leading-relaxed my-6"
                >
                  {block.items.map((it, j) => (
                    <li key={j}>{it}</li>
                  ))}
                </ul>
              );
            }
            if (block.type === "quote") {
              return (
                <blockquote
                  key={i}
                  className="border-l-2 border-primary pl-6 my-10 italic text-xl text-foreground/80 font-light leading-relaxed"
                >
                  <p>{block.text}</p>
                  {block.cite && (
                    <cite className="block not-italic text-xs uppercase tracking-[0.3em] text-muted-foreground mt-4">
                      {block.cite}
                    </cite>
                  )}
                </blockquote>
              );
            }
            return (
              <p key={i} className="text-foreground/85 leading-relaxed text-lg mb-5">
                {block.text}
              </p>
            );
          })}

          <div className="mt-16 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-[0.25em] border border-border px-3 py-1.5 text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="mt-16 p-8 bg-secondary/40 border border-border">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">— Plan your journey</p>
            <h3 className="font-display text-2xl mb-3 text-foreground">
              Travel Cambodia with a private French-speaking guide
            </h3>
            <p className="text-muted-foreground mb-5 leading-relaxed">
              Tailor-made tours from 1 to 30 days — Angkor Wat, Phnom Kulen, the Tonlé Sap and beyond.
            </p>
            <Link
              to="/tours"
              className="inline-block bg-foreground text-background px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-primary transition-colors"
            >
              Browse all tours
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-20 md:py-24 bg-secondary/30 border-t border-border">
          <div className="container max-w-5xl">
            <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-10">
              — Keep reading
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden mb-5 aspect-[5/4] bg-secondary">
                    <img
                      src={p.cover}
                      alt={p.coverAlt}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h4 className="font-display text-xl md:text-2xl leading-tight text-foreground group-hover:text-primary transition-colors">
                    {p.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
};

export default BlogPost;
